import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import JSZip from "jszip";
import { useEditor } from "./core/EditorContext";
import { deriveTopology, meshBounds, uvDiagnostics } from "./core/mesh";
import { decodeProject, downloadBytes, encodeProject, loadRecovery, saveRecovery, sceneSummary } from "./io/project";
import { exportFbxAscii, importFbx } from "./io/fbx";
import { Viewport, type ViewportApi } from "./render/Viewport";
import type { CommandRequest, EditorSnapshot, MaterialData, SelectionMode, ToolMode, Vec3 } from "./types";

const toolButtons: Array<{ tool: ToolMode; key: string; label: string; icon: string }> = [
  { tool: "select", key: "Q", label: "Select", icon: "↖" },
  { tool: "translate", key: "W", label: "Move", icon: "✥" },
  { tool: "rotate", key: "E", label: "Rotate", icon: "◉" },
  { tool: "scale", key: "R", label: "Scale", icon: "⤢" },
];

const selectionButtons: Array<{ mode: SelectionMode; key: string }> = [
  { mode: "object", key: "F8" },
  { mode: "vertex", key: "F9" },
  { mode: "edge", key: "F10" },
  { mode: "face", key: "F11" },
];

function NumericField({ label, value, onCommit, step = 1, min, max }: {
  label: string;
  value: number;
  onCommit: (value: number) => void;
  step?: number;
  min?: number;
  max?: number;
}) {
  const [draft, setDraft] = useState(String(Number(value.toFixed(4))));
  useEffect(() => setDraft(String(Number(value.toFixed(4)))), [value]);
  const commit = () => {
    const parsed = Number(draft);
    if (Number.isFinite(parsed)) onCommit(parsed);
    else setDraft(String(value));
  };
  return (
    <label className="numeric-field">
      <span>{label}</span>
      <input
        type="number"
        value={draft}
        step={step}
        min={min}
        max={max}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => { if (event.key === "Enter") event.currentTarget.blur(); }}
      />
    </label>
  );
}

function Vec3Editor({ label, value, onCommit }: { label: string; value: Vec3; onCommit: (value: Vec3) => void }) {
  return (
    <div className="vec-row">
      <span>{label}</span>
      {(["X", "Y", "Z"] as const).map((axis, index) => (
        <NumericField key={axis} label={axis} value={value[index]} step={0.1} onCommit={(number) => {
          const next = [...value] as Vec3;
          next[index] = number;
          onCommit(next);
        }} />
      ))}
    </div>
  );
}

function ActionPanel() {
  const { liveAction, updateLiveAction, commitLiveAction } = useEditor();
  if (!liveAction) return (
    <section className="panel-section action-empty">
      <div className="section-title"><span>CURRENT ACTION</span><em>READY</em></div>
      <p>Select components, then use Extrude, Bevel, or Insert Loop. The newest action remains editable here.</p>
    </section>
  );
  const params = liveAction.params;
  const numeric = (key: string, label: string, step = 1, min?: number, max?: number) => (
    <NumericField label={label} value={Number(params[key] ?? 0)} step={step} min={min} max={max} onCommit={(value) => void updateLiveAction({ [key]: value })} />
  );
  return (
    <section className="panel-section current-action" data-testid="current-action">
      <div className="section-title"><span>{liveAction.label.toUpperCase()}</span><em>LIVE</em></div>
      <div className="field-grid">
        {liveAction.type === "extrude" && <>
          {numeric("distance", "Distance", 1)}
          {numeric("offset", "Offset", 0.5)}
          {numeric("divisions", "Divisions", 1, 1, 32)}
          <label className="toggle-field"><input type="checkbox" checked={Boolean(params.keepTogether)} onChange={(event) => void updateLiveAction({ keepTogether: event.target.checked })} /><span>Keep faces together</span></label>
        </>}
        {liveAction.type === "bevel" && <>
          {numeric("width", "Width", 0.5, 0.001)}
          {numeric("segments", "Segments", 1, 1, 16)}
        </>}
        {liveAction.type === "edgeLoop" && <>
          {numeric("position", "Position", 0.05, 0.02, 0.98)}
          {numeric("count", "Loops", 1, 1, 16)}
        </>}
        {(liveAction.type === "boxUV" || liveAction.type === "planarUV") && <>
          {numeric("scale", "Scale", 0.1, 0.01)}
          {numeric("rotation", "Rotate", 5)}
          {numeric("offsetU", "Offset U", 0.05)}
          {numeric("offsetV", "Offset V", 0.05)}
        </>}
      </div>
      <button className="wide-button" onClick={commitLiveAction}>COMMIT ACTION</button>
    </section>
  );
}

function Outliner() {
  const { snapshot, setSelection, execute } = useEditor();
  return (
    <section className="panel-section outliner">
      <div className="section-title"><span>OUTLINER</span><em>{snapshot.scene.objects.length} OBJECTS</em></div>
      <div className="outliner-list">
        {snapshot.scene.objects.map((object) => {
          const active = snapshot.selection.objectIds.includes(object.id);
          return (
            <button key={object.id} className={active ? "active" : ""} onClick={(event) => {
              const ids = event.shiftKey ? [...new Set([...snapshot.selection.objectIds, object.id])] : [object.id];
              setSelection({ mode: "object", objectIds: ids, componentIds: [] });
            }}>
              <span className="mesh-icon">◇</span>
              <span>{object.name}</span>
              <small>{object.faces.length}F</small>
            </button>
          );
        })}
      </div>
      <div className="row-buttons">
        <button onClick={() => void execute({ id: crypto.randomUUID(), type: "scene.duplicateObject", args: {} })}>DUPLICATE</button>
        <button onClick={() => void execute({ id: crypto.randomUUID(), type: "scene.deleteObjects", args: {} })}>DELETE</button>
      </div>
    </section>
  );
}

function TransformPanel() {
  const { snapshot, execute } = useEditor();
  const object = snapshot.scene.objects.find((candidate) => candidate.id === snapshot.scene.activeObjectId);
  if (!object) return null;
  const update = (patch: Record<string, unknown>) => void execute({ id: crypto.randomUUID(), type: "object.setTransform", args: { objectId: object.id, ...patch } });
  return (
    <section className="panel-section transform-panel">
      <div className="section-title"><span>TRANSFORM</span><em>{snapshot.tools.space.toUpperCase()}</em></div>
      <Vec3Editor label="Move" value={object.transform.position} onCommit={(position) => update({ position })} />
      <Vec3Editor label="Rotate" value={object.transform.rotation.map((value) => value * 180 / Math.PI) as Vec3} onCommit={(degrees) => update({ rotation: degrees.map((value) => value * Math.PI / 180) })} />
      <Vec3Editor label="Scale" value={object.transform.scale} onCommit={(scale) => update({ scale })} />
      <Vec3Editor label="Pivot" value={object.transform.pivot} onCommit={(pivot) => update({ pivot })} />
    </section>
  );
}

function MaterialsUvPanel() {
  const { snapshot, execute, setDisplay } = useEditor();
  const [tab, setTab] = useState<"material" | "uv">("material");
  const [activeMaterialId, setActiveMaterialId] = useState(snapshot.scene.materials[0]?.id);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeMaterial = snapshot.scene.materials.find((material) => material.id === activeMaterialId) ?? snapshot.scene.materials[0];
  const activeMesh = snapshot.scene.objects.find((object) => object.id === snapshot.scene.activeObjectId);
  const diagnostics = activeMesh ? uvDiagnostics(activeMesh) : null;
  const updateMaterial = (patch: Partial<MaterialData>) => {
    if (!activeMaterial) return;
    void execute({ id: crypto.randomUUID(), type: "material.update", args: { materialId: activeMaterial.id, ...patch } });
  };
  const addTexture = async (file: File) => {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
    const before = snapshot.scene.textures.length;
    await execute({ id: crypto.randomUUID(), type: "texture.add", args: { name: file.name, mimeType: file.type, dataUrl } });
    const nextTextureId = snapshot.scene.textures[before]?.id;
    window.setTimeout(() => {
      const current = window.feral3d?.getSceneSummary() as { textures?: Array<{ id: string }> } | undefined;
      const textureId = current?.textures?.at(-1)?.id ?? nextTextureId;
      if (textureId) updateMaterial({ baseColorTextureId: textureId });
      setDisplay("textured");
    }, 50);
  };
  return (
    <section className="panel-section material-panel">
      <div className="tab-row">
        <button className={tab === "material" ? "active" : ""} onClick={() => setTab("material")}>MATERIAL</button>
        <button className={tab === "uv" ? "active" : ""} onClick={() => setTab("uv")}>UV</button>
      </div>
      {tab === "material" && activeMaterial && <>
        <select value={activeMaterial.id} onChange={(event) => setActiveMaterialId(event.target.value)}>
          {snapshot.scene.materials.map((material) => <option key={material.id} value={material.id}>{material.name}</option>)}
        </select>
        <label className="color-field"><span>Base color</span><input type="color" value={activeMaterial.color} onChange={(event) => updateMaterial({ color: event.target.value })} /></label>
        <NumericField label="Roughness" value={activeMaterial.roughness} step={0.05} min={0} max={1} onCommit={(roughness) => updateMaterial({ roughness })} />
        <NumericField label="Metallic" value={activeMaterial.metallic} step={0.05} min={0} max={1} onCommit={(metallic) => updateMaterial({ metallic })} />
        <input ref={inputRef} hidden type="file" accept="image/*" onChange={(event) => { const file = event.target.files?.[0]; if (file) void addTexture(file); }} />
        <button className="wide-button" onClick={() => inputRef.current?.click()}>LOAD BASE-COLOR IMAGE</button>
        <button className="wide-button accent" onClick={() => void execute({ id: crypto.randomUUID(), type: "material.assign", args: { materialId: activeMaterial.id } })}>ASSIGN TO {snapshot.selection.mode === "face" ? "FACES" : "OBJECT"}</button>
      </>}
      {tab === "uv" && <>
        <div className="uv-preview" aria-label="UV checker preview">
          {activeMesh?.faces.slice(0, 80).map((face) => {
            const points = face.uvs.map((uv) => `${uv[0] * 100},${100 - uv[1] * 100}`).join(" ");
            return <svg key={face.id} viewBox="0 0 100 100" preserveAspectRatio="none"><polygon points={points} /></svg>;
          })}
          {!activeMesh && <span>NO MESH</span>}
        </div>
        <div className="uv-stats">
          <span>{diagnostics?.triangleCount ?? 0} TRI</span>
          <span>{diagnostics?.outOfRange ?? 0} OUTSIDE</span>
          <span>{diagnostics?.degenerate ?? 0} DEGENERATE</span>
        </div>
        <button className="wide-button accent" onClick={() => void execute({ id: crypto.randomUUID(), type: "uv.projectBox", args: { scale: 1, rotation: 0, offsetU: 0, offsetV: 0 } })}>BOX PROJECT</button>
        <div className="row-buttons">
          {(["x", "y", "z"] as const).map((axis) => <button key={axis} onClick={() => void execute({ id: crypto.randomUUID(), type: "uv.projectPlanar", args: { axis, scale: 1, rotation: 0, offsetU: 0, offsetV: 0 } })}>{axis.toUpperCase()} PLANAR</button>)}
        </div>
        <button className="wide-button" onClick={() => setDisplay("checker")}>SHOW CHECKER</button>
      </>}
    </section>
  );
}

function HistoryPanel() {
  const { snapshot } = useEditor();
  return (
    <section className="panel-section history-panel">
      <div className="section-title"><span>RECORDED HISTORY</span><em>READ ONLY</em></div>
      <ol>
        {[...snapshot.scene.operations].reverse().slice(0, 12).map((operation) => (
          <li key={operation.id}><span>{operation.label}</span><small>r{operation.resultRevision}</small></li>
        ))}
        {!snapshot.scene.operations.length && <li className="empty">No recorded actions yet.</li>}
      </ol>
    </section>
  );
}

export default function App() {
  const editor = useEditor();
  const { snapshot, getSnapshot, execute, setSelectionMode, setTool, setSpace, setDisplay, setQuadView, setPivotEditing, undo, redo, replaceSnapshot } = editor;
  const viewportApiRef = useRef<ViewportApi | null>(null);
  const [fileMenu, setFileMenu] = useState(false);
  const [automation, setAutomation] = useState<{ enabled: boolean; detail?: string }>({ enabled: false });
  const openInputRef = useRef<HTMLInputElement>(null);
  const fbxInputRef = useRef<HTMLInputElement>(null);

  const onViewportReady = useCallback((api: ViewportApi | null) => { viewportApiRef.current = api; }, []);
  const activeMesh = snapshot.scene.objects.find((object) => object.id === snapshot.scene.activeObjectId);
  const topology = activeMesh ? deriveTopology(activeMesh) : null;
  const bounds = activeMesh ? meshBounds(activeMesh) : null;

  const saveProject = useCallback(async () => {
    const bytes = await encodeProject(snapshot);
    if (window.feralDesktop) await window.feralDesktop.saveProject(bytes, `${activeMesh?.name ?? "untitled"}.feral3d`);
    else downloadBytes(bytes, `${activeMesh?.name ?? "untitled"}.feral3d`, "application/x-feral3d");
  }, [snapshot, activeMesh?.name]);

  const loadProjectBytes = useCallback(async (bytes: Uint8Array) => {
    const loaded = await decodeProject(bytes);
    replaceSnapshot(loaded);
  }, [replaceSnapshot]);

  const openProject = useCallback(async () => {
    if (window.feralDesktop) {
      const file = await window.feralDesktop.openProject();
      if (file) await loadProjectBytes(new Uint8Array(file.bytes));
    } else openInputRef.current?.click();
  }, [loadProjectBytes]);

  const exportFbx = useCallback(async (bundleTextures = false) => {
    const fbx = exportFbxAscii(snapshot.scene);
    const name = `${activeMesh?.name ?? "feral3d-export"}.fbx`;
    if (bundleTextures && snapshot.scene.textures.length) {
      const zip = new JSZip();
      zip.file(name, fbx);
      snapshot.scene.textures.forEach((texture) => {
        const base64 = texture.dataUrl.split(",")[1];
        const extension = texture.mimeType.split("/")[1] || "png";
        zip.file(`textures/${texture.name.replace(/\.[^.]+$/, "")}.${extension}`, base64, { base64: true });
      });
      const bytes = await zip.generateAsync({ type: "uint8array", compression: "DEFLATE" });
      if (window.feralDesktop) await window.feralDesktop.exportFile(bytes, `${activeMesh?.name ?? "feral3d-export"}-fbx.zip`, [{ name: "ZIP archive", extensions: ["zip"] }]);
      else downloadBytes(bytes, `${activeMesh?.name ?? "feral3d-export"}-fbx.zip`, "application/zip");
      return;
    }
    if (window.feralDesktop) await window.feralDesktop.exportFile(fbx, name, [{ name: "Autodesk FBX", extensions: ["fbx"] }]);
    else downloadBytes(fbx, name, "application/octet-stream");
  }, [snapshot.scene, activeMesh?.name]);

  const importFbxFile = useCallback(async (file: File) => {
    const imported = await importFbx(await file.arrayBuffer(), file.name);
    for (const material of imported.materials) {
      await execute({ id: crypto.randomUUID(), type: "material.create", args: { ...material } });
    }
    for (const mesh of imported.meshes) await execute({ id: crypto.randomUUID(), type: "mesh.replaceFromImport", args: { mesh } });
  }, [execute]);

  const captureViewport = useCallback(async () => {
    const capture = viewportApiRef.current?.capture();
    if (!capture) throw new Error("Viewport capture is not ready.");
    return {
      ...capture,
      scene: sceneSummary(snapshot.scene),
      selection: snapshot.selection,
      uv: activeMesh ? uvDiagnostics(activeMesh) : null,
    };
  }, [snapshot, activeMesh]);

  useEffect(() => {
    const timer = window.setTimeout(() => void saveRecovery(snapshot).catch(() => undefined), 700);
    return () => window.clearTimeout(timer);
  }, [snapshot.scene.revision]);

  useEffect(() => {
    const api = {
      execute: (request: CommandRequest) => execute(request),
      getSceneSummary: () => {
        const current = getSnapshot();
        return { ...sceneSummary(current.scene), textures: current.scene.textures.map((texture) => ({ id: texture.id, name: texture.name })) };
      },
      captureViewport: captureViewport,
    };
    window.feral3d = api;
    return () => { delete window.feral3d; };
  }, [execute, getSnapshot, snapshot, captureViewport]);

  useEffect(() => {
    if (!window.feralDesktop) return;
    return window.feralDesktop.onAutomationCommand(async ({ id, request }) => {
      try {
        if (request.type === "viewport.capture") window.feralDesktop?.respondAutomation(id, { ok: true, result: await captureViewport() });
        else if (request.type === "scene.summary") window.feralDesktop?.respondAutomation(id, { ok: true, result: sceneSummary(snapshot.scene) });
        else window.feralDesktop?.respondAutomation(id, await execute(request));
      } catch (error) {
        window.feralDesktop?.respondAutomation(id, { ok: false, error: error instanceof Error ? error.message : String(error) });
      }
    });
  }, [execute, snapshot.scene, captureViewport]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest("input,select,textarea,[contenteditable=true]")) return;
      const key = event.key.toLowerCase();
      if (event.ctrlKey && key === "s") { event.preventDefault(); void saveProject(); return; }
      if (event.ctrlKey && key === "z") { event.preventDefault(); event.shiftKey ? redo() : undo(); return; }
      if (event.ctrlKey && key === "y") { event.preventDefault(); redo(); return; }
      if (event.ctrlKey && key === "e") {
        event.preventDefault();
        if (snapshot.selection.mode === "face" || snapshot.selection.mode === "edge") void execute({ id: crypto.randomUUID(), type: "mesh.extrude", args: { mode: snapshot.selection.mode, distance: 20, divisions: 1, offset: 0, keepTogether: true } });
        return;
      }
      if (event.ctrlKey && key === "b") {
        event.preventDefault();
        if (snapshot.selection.mode === "edge") void execute({ id: crypto.randomUUID(), type: "mesh.bevel", args: { width: 5, segments: 1 } });
        return;
      }
      if (key === "q") setTool("select");
      if (key === "w") setTool("translate");
      if (key === "e") setTool("rotate");
      if (key === "r") setTool("scale");
      if (event.key === "F8") { event.preventDefault(); setSelectionMode("object"); }
      if (event.key === "F9") { event.preventDefault(); setSelectionMode("vertex"); }
      if (event.key === "F10") { event.preventDefault(); setSelectionMode("edge"); }
      if (event.key === "F11") { event.preventDefault(); setSelectionMode("face"); }
      if (key === "4") setDisplay("wireframe");
      if (key === "5") setDisplay("shaded");
      if (key === "6") setDisplay("textured");
      if (key === "f") viewportApiRef.current?.frameSelection();
      if (key === "a") viewportApiRef.current?.frameAll();
      if (key === " " && !event.repeat) { event.preventDefault(); setQuadView(!snapshot.tools.quadView); }
      if (key === "d" && !event.repeat) setPivotEditing(true);
      if (key === "insert" && !event.repeat) setPivotEditing(!snapshot.tools.pivotEditing);
      if (key === "delete") void execute({ id: crypto.randomUUID(), type: "scene.deleteObjects", args: {} });
      if (key === "escape") setPivotEditing(false);
    };
    const onKeyUp = (event: KeyboardEvent) => { if (event.key.toLowerCase() === "d") setPivotEditing(false); };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => { window.removeEventListener("keydown", onKeyDown); window.removeEventListener("keyup", onKeyUp); };
  }, [snapshot, execute, saveProject, undo, redo, setTool, setSelectionMode, setDisplay, setQuadView, setPivotEditing]);

  const toggleAutomation = async () => {
    if (!window.feralDesktop) {
      setAutomation({ enabled: true, detail: "Browser command API exposed as window.feral3d" });
      return;
    }
    if (automation.enabled) {
      await window.feralDesktop.disableAutomation();
      setAutomation({ enabled: false });
    } else {
      const result = await window.feralDesktop.enableAutomation();
      setAutomation({ enabled: result.enabled, detail: result.error ?? result.pipe });
    }
  };

  return (
    <main className="app-shell" onClick={() => fileMenu && setFileMenu(false)}>
      <header className="app-header">
        <div className="brand"><span className="brand-mark">F3</span><strong>FERAL3D</strong><small>POLYGON MODELER</small></div>
        <nav className="main-menu">
          <div className="menu-wrap"><button onClick={(event) => { event.stopPropagation(); setFileMenu(!fileMenu); }}>FILE</button>
            {fileMenu && <div className="dropdown" onClick={(event) => event.stopPropagation()}>
              <button onClick={() => { void openProject(); setFileMenu(false); }}>Open project… <kbd>Ctrl+O</kbd></button>
              <button onClick={() => { void saveProject(); setFileMenu(false); }}>Save project <kbd>Ctrl+S</kbd></button>
              <hr />
              <button onClick={() => { fbxInputRef.current?.click(); setFileMenu(false); }}>Import FBX…</button>
              <button onClick={() => { void exportFbx(false); setFileMenu(false); }}>Export FBX…</button>
              <button onClick={() => { void exportFbx(true); setFileMenu(false); }}>Export FBX + textures ZIP…</button>
            </div>}
          </div>
          <button onClick={undo} disabled={!editor.canUndo}>UNDO</button>
          <button onClick={redo} disabled={!editor.canRedo}>REDO</button>
          <button onClick={() => void execute({ id: crypto.randomUUID(), type: "scene.createCube", args: { size: 100, divisions: [1, 1, 1] } })}>CREATE CUBE</button>
        </nav>
        <div className="header-actions">
          <button className={automation.enabled ? "automation active" : "automation"} onClick={() => void toggleAutomation()}><span />{automation.enabled ? "LLM CONNECTED" : "ENABLE LLM"}</button>
          <button onClick={() => void captureViewport().then((capture) => downloadBytes(Uint8Array.from(atob(capture.dataUrl.split(",")[1]), (char) => char.charCodeAt(0)), "feral3d-viewport.png", "image/png"))}>CAPTURE PNG</button>
        </div>
      </header>

      <section className="mode-strip">
        <div className="selection-modes">
          {selectionButtons.map(({ mode, key }) => <button key={mode} className={snapshot.selection.mode === mode ? "active" : ""} onClick={() => setSelectionMode(mode)}>{mode.toUpperCase()} <kbd>{key}</kbd></button>)}
        </div>
        <div className="modeling-actions">
          <button disabled={!(["face", "edge"] as SelectionMode[]).includes(snapshot.selection.mode) || !snapshot.selection.componentIds.length} onClick={() => void execute({ id: crypto.randomUUID(), type: "mesh.extrude", args: { mode: snapshot.selection.mode, distance: 20, divisions: 1, offset: 0, keepTogether: true } })}>EXTRUDE <kbd>Ctrl+E</kbd></button>
          <button disabled={snapshot.selection.mode !== "edge" || !snapshot.selection.componentIds.length} onClick={() => void execute({ id: crypto.randomUUID(), type: "mesh.bevel", args: { width: 5, segments: 1 } })}>BEVEL <kbd>Ctrl+B</kbd></button>
          <button disabled={snapshot.selection.mode !== "edge" || !snapshot.selection.componentIds.length} onClick={() => void execute({ id: crypto.randomUUID(), type: "mesh.insertEdgeLoop", args: { edgeId: snapshot.selection.componentIds[0], position: 0.5, count: 1 } })}>INSERT LOOP</button>
        </div>
        <div className="view-actions">
          <button className={snapshot.tools.space === "world" ? "active" : ""} onClick={() => setSpace("world")}>WORLD</button>
          <button className={snapshot.tools.space === "local" ? "active" : ""} onClick={() => setSpace("local")}>LOCAL</button>
          <span className="separator" />
          {(["wireframe", "shaded", "textured"] as const).map((mode, index) => <button key={mode} className={snapshot.tools.display === mode ? "active" : ""} onClick={() => setDisplay(mode)}>{index + 4}</button>)}
          <button className={snapshot.tools.quadView ? "active" : ""} onClick={() => setQuadView(!snapshot.tools.quadView)}>4 VIEW</button>
        </div>
      </section>

      <section className="workspace">
        <aside className="toolbox">
          {toolButtons.map((item) => <button key={item.tool} title={`${item.label} (${item.key})`} className={snapshot.tools.tool === item.tool ? "active" : ""} onClick={() => setTool(item.tool)}><span>{item.icon}</span><kbd>{item.key}</kbd></button>)}
          <div className="tool-separator" />
          <button title="Edit pivot (hold D)" className={snapshot.tools.pivotEditing ? "active pivot" : ""} onClick={() => setPivotEditing(!snapshot.tools.pivotEditing)}><span>⌖</span><kbd>D</kbd></button>
        </aside>

        <div className="viewport-column">
          <Viewport onReady={onViewportReady} />
          <footer className="status-bar">
            <span className="status-message">{editor.lastMessage}</span>
            <span>{snapshot.selection.mode.toUpperCase()} · {snapshot.selection.componentIds.length || snapshot.selection.objectIds.length} SELECTED</span>
            <span>{activeMesh ? `${activeMesh.vertices.length}V  ${topology?.edges.length ?? 0}E  ${activeMesh.faces.length}F` : "EMPTY SCENE"}</span>
            <span>REV {snapshot.scene.revision}</span>
          </footer>
        </div>

        <aside className="inspector">
          <Outliner />
          <ActionPanel />
          <TransformPanel />
          <MaterialsUvPanel />
          <HistoryPanel />
        </aside>
      </section>

      <input ref={openInputRef} hidden type="file" accept=".feral3d" onChange={(event) => { const file = event.target.files?.[0]; if (file) void file.arrayBuffer().then((bytes) => loadProjectBytes(new Uint8Array(bytes))); }} />
      <input ref={fbxInputRef} hidden type="file" accept=".fbx" onChange={(event) => { const file = event.target.files?.[0]; if (file) void importFbxFile(file); }} />
      {automation.detail && <div className="toast" onClick={() => setAutomation((state) => ({ ...state, detail: undefined }))}>{automation.detail}</div>}
    </main>
  );
}
